import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { useTranslation } from "react-i18next";
import {
  AdminAudienceViewDto,
  AdminReviewSessionViewDto,
  AudienceWithUrlDto,
  AnyoneLinkStateDto,
  CreateFlyerReviewAudienceDto,
  RegenerateAudienceTokenDto,
  RegenerateAudienceTokenResponseDto,
  SetAnyoneLinkDto,
  UpdateFlyerReviewAudienceDto,
} from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../../locales/messages";
import { reviewTokens } from "../../theme";
import { AddAudienceForm } from "./AddAudienceForm";
import { AudienceRow } from "./AudienceRow";
import { AnyoneLinkSection } from "./AnyoneLinkSection";

interface Props {
  open: boolean;
  onClose: () => void;
  bootstrap?: AdminReviewSessionViewDto;
  inviteAudience: (
    body: CreateFlyerReviewAudienceDto,
  ) => Promise<AudienceWithUrlDto | undefined>;
  updateAudience: (
    audienceId: string,
    body: UpdateFlyerReviewAudienceDto,
  ) => Promise<boolean | undefined>;
  regenerateAudienceToken: (
    audienceId: string,
    body: RegenerateAudienceTokenDto,
  ) => Promise<RegenerateAudienceTokenResponseDto | undefined>;
  revokeAudience: (audienceId: string) => Promise<boolean | undefined>;
  setAnyoneLink: (
    body: SetAnyoneLinkDto,
  ) => Promise<AnyoneLinkStateDto | undefined>;
}

const SectionLabel = ({
  label,
  count,
}: {
  label: string;
  count?: number;
}) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
    <Typography
      variant="caption"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "text.secondary",
      }}
    >
      {label}
    </Typography>
    {typeof count === "number" ? (
      <Box
        sx={{
          px: 0.75,
          py: 0.125,
          borderRadius: `${reviewTokens.radiusSm}px`,
          bgcolor: reviewTokens.slate100,
          color: "text.secondary",
          fontSize: 11,
          fontWeight: 600,
          minWidth: 18,
          textAlign: "center",
        }}
      >
        {count}
      </Box>
    ) : null}
  </Stack>
);

export function WhoHasAccessModal({
  open,
  onClose,
  bootstrap,
  inviteAudience,
  updateAudience,
  regenerateAudienceToken,
  revokeAudience,
  setAnyoneLink,
}: Props) {
  const { t } = useTranslation();
  const audiences = bootstrap?.audiences || [];
  const activeAudiences = audiences.filter(
    (a) =>
      !a.revokedAt &&
      a.permission !== AdminAudienceViewDto.PermissionEnum.REVOKED,
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${reviewTokens.radiusLg}px`,
          overflow: "hidden",
        },
      }}
      data-testid="who-has-access-modal"
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: `${reviewTokens.radiusSm}px`,
              bgcolor: reviewTokens.brandSoft,
              color: reviewTokens.brand,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PeopleOutlineIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              component="h2"
              sx={{ fontWeight: 600, fontSize: "0.9375rem", lineHeight: 1.3 }}
            >
              {t(messages.Who_Has_Access())}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t(messages.Review_Active_Audiences(), {
                count: activeAudiences.length,
              })}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          data-testid="who-has-access-close"
          aria-label="close"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 3,
          bgcolor: reviewTokens.bgPage,
        }}
      >
        <Box
          sx={{
            p: 2.5,
            border: 1,
            borderColor: "divider",
            borderRadius: `${reviewTokens.radiusLg}px`,
            bgcolor: "background.paper",
            mb: 2,
          }}
        >
          <SectionLabel label={t(messages.Invite_Reviewer())} />
          <AddAudienceForm onInvite={inviteAudience} />
        </Box>

        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: `${reviewTokens.radiusLg}px`,
            bgcolor: "background.paper",
            mb: 2,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2.5, pt: 2 }}>
            <SectionLabel label="People with access" count={audiences.length} />
          </Box>
          {audiences.length === 0 ? (
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t(messages.No_Audiences_Yet())}
              </Typography>
            </Box>
          ) : (
            <Box>
              {audiences.map((audience) => (
                <AudienceRow
                  key={audience.id}
                  audience={audience}
                  onUpdate={updateAudience}
                  onRegenerate={regenerateAudienceToken}
                  onRevoke={revokeAudience}
                />
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            p: 2.5,
            border: 1,
            borderColor: "divider",
            borderRadius: `${reviewTokens.radiusLg}px`,
            bgcolor: "background.paper",
          }}
        >
          <AnyoneLinkSection
            anyoneLink={bootstrap?.anyoneLink}
            onSet={setAnyoneLink}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
